import React, { createContext, useState, useEffect, useCallback, ReactNode, useContext } from 'react';
import { ISiteMode } from '../interfaces/heavyDutyMachinery/ISiteMode';
import { getSiteMode } from '../services/SiteModeService';

interface SiteModeContextProps {
  siteMode: ISiteMode;
  /** True until the first answer lands. Screens that would flash the wrong face wait on it. */
  loading: boolean;
  /** Re-reads the flags -- used by the admin panel right after flipping one. */
  refresh: () => Promise<void>;
}

interface SiteModeProviderProps {
  children: ReactNode;
}

/**
 * Before the answer arrives, the app behaves as if it were fully open. Every
 * consumer that would show something different waits on `loading` instead of
 * rendering this, so it is never on screen -- it exists so the context has a
 * shape from the first render and nothing has to null-check it.
 */
const OPEN: ISiteMode = { landingModeEnabled: false, registrationEnabled: true };

const SiteModeContext = createContext<SiteModeContextProps | undefined>(undefined);

/**
 * Which face the site is wearing, asked once per page load.
 *
 * Unlike every other provider in this file's neighbourhood, this one does NOT
 * depend on AuthProvider: the whole point is to answer for a stranger, before
 * anybody has signed in, and while registration may not even be open.
 *
 * It is a convenience, never a gate. Landing mode only decides what an
 * anonymous visitor SEES; the thing that actually has to hold -- that nobody
 * new can create an account -- is enforced inside the API's own
 * AuthController.Register, which refuses the POST whatever this says.
 */
export const SiteModeProvider: React.FC<SiteModeProviderProps> = ({ children }) => {
  const [siteMode, setSiteMode] = useState<ISiteMode>(OPEN);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // getSiteMode never throws -- it falls back to "open" on a failed request
    // rather than replacing a working app with a "coming soon" page over a
    // dropped connection. See its own doc comment.
    setSiteMode(await getSiteMode());
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return <SiteModeContext.Provider value={{ siteMode, loading, refresh }}>{children}</SiteModeContext.Provider>;
};

export const useSiteMode = (): SiteModeContextProps => {
  const context = useContext(SiteModeContext);
  if (!context) throw new Error('useSiteMode must be used within a SiteModeProvider');
  return context;
};
